/**
 * Every documented 4xx of the reconnect endpoints as a discriminated union the
 * screens switch on. Classified on `(HTTP status, detail.status code)` — the
 * stable machine CODE — and never on the human `message` or any substring of
 * it. An unrecognised pair is `other`, never a guessed kind.
 *
 * **Body shapes and where each came from.** Verified 2026-10-02 against the
 * backend's `origin/develop` (`api/src/api/v1/endpoints/aggregator.py`,
 * `_check_relink_session`/`_relink_session_mismatch`, read-only) and its
 * `docs/frontend-integration/bank-reconnect.md`; the wire fixtures in
 * `test/fixtures/wire/` cover the rest.
 *
 * - `404` — `{detail: "Item not found"}`: a STRING. Fixture
 *   `error-404-relink-item-not-found.json`.
 * - `409 use_reauth` / `409 already_relinked` —
 *   `{detail: {status, item_id, message}}`. Fixtures. `item_id` is the live
 *   item (use_reauth) or the reconnect that won (already_relinked).
 * - `409 relink_completed` — `{detail: {status, message}}`. Fixture.
 * - `422` too many — a FastAPI LIST of `{type: 'too_long', …}`. Fixture.
 * - `400 relink_session_mismatch` / `relink_provider_mismatch` —
 *   `{detail: {status, message}}`. **No fixture exists** (contract gap): the
 *   shape is taken from the backend source, not from a captured response.
 * - `400` nonce — a STRING detail. **The fixture and the code disagree on the
 *   wording** (fixture: "State nonce is missing, expired, or has already been
 *   used."; code: "Invalid or expired state nonce"), so the string is never
 *   read: ANY 400 whose `detail` is a string is `nonce_invalid`.
 * - `409 account_changed` / `already_paired`, `422 currency_mismatch`,
 *   `409 already_linked` (Flinks) — object detail with `status`, taken from the
 *   guide's tables; no fixtures.
 * - A successful relink COMPLETION has no fixture either. Its shape, from the
 *   guide §4 and `RelinkInfo` in the generated schema: the usual
 *   `{status, provider_item_id, …}` plus `relink: {from_item_id, status}`, where
 *   `provider_item_id` is the NEW item.
 */
export type RelinkError =
  | { kind: 'not_found' }
  | { kind: 'use_reauth'; itemId: string }
  | { kind: 'already_relinked'; itemId: string }
  | { kind: 'session_mismatch' }
  | { kind: 'provider_mismatch' }
  | { kind: 'nonce_invalid' }
  | { kind: 'already_linked' }
  | { kind: 'account_changed' }
  | { kind: 'already_paired' }
  | { kind: 'relink_completed' }
  | { kind: 'currency_mismatch' }
  | { kind: 'too_many' }
  | { kind: 'other'; status: number }

function detailOf(body: unknown): unknown {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>).detail : undefined
}

function field(detail: unknown, key: string): unknown {
  return typeof detail === 'object' && detail !== null && !Array.isArray(detail)
    ? (detail as Record<string, unknown>)[key]
    : undefined
}

export function relinkErrorOf(status: number, body: unknown): RelinkError {
  const detail = detailOf(body)
  const code = field(detail, 'status')
  const itemId = field(detail, 'item_id')

  if (status === 404) return { kind: 'not_found' }

  if (status === 400) {
    if (typeof detail === 'string') return { kind: 'nonce_invalid' }
    if (code === 'relink_session_mismatch') return { kind: 'session_mismatch' }
    if (code === 'relink_provider_mismatch') return { kind: 'provider_mismatch' }
  }

  if (status === 409) {
    if (code === 'use_reauth' && typeof itemId === 'string') return { kind: 'use_reauth', itemId }
    if (code === 'already_relinked' && typeof itemId === 'string') {
      return { kind: 'already_relinked', itemId }
    }
    if (code === 'already_linked') return { kind: 'already_linked' }
    if (code === 'account_changed') return { kind: 'account_changed' }
    if (code === 'already_paired') return { kind: 'already_paired' }
    if (code === 'relink_completed') return { kind: 'relink_completed' }
  }

  if (status === 422) {
    if (code === 'currency_mismatch') return { kind: 'currency_mismatch' }
    if (
      Array.isArray(detail) &&
      detail.some((e) => typeof e === 'object' && e !== null && (e as { type?: unknown }).type === 'too_long')
    ) {
      return { kind: 'too_many' }
    }
  }

  return { kind: 'other', status }
}
